import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import {
    ContainedButton,
    Text,
    StyledCard,
    StyledCardContent,
    SignInForm,
    Checkbox,
    Span,
} from '../../components';

import SelectHospital from '../../components/Hospital/SelectHospital';
import ReCAPTCHA from '../../components/ReCAPTCHA';

import {
    signInWithPassword,
    requestOTP,
    verifyOTP,
    completeRegister,
    checkConfirm,
    signInWithOTP,
} from '../../services';

import { alertError } from '../../redux/stateActions';

import { validPhone, validEmail, validPassword } from '../../utils/regex';

const FirdayLogo = '/resources/images/friday_logo.svg';
import socket from '../../socket';

const STATE = Object.freeze({
    PASSWORD: 'PASSWORD',
    REQUEST: 'REQUEST',
    OTP: 'OTP',
    CONFIRM_PASSWORD: 'CONFIRM_PASSWORD',
    TERM: 'TERM',
    SELECT: 'SELECT',
});

const SignIn = ({ location, history }) => {
    const dispatch = useDispatch();
    const mobileS = useMediaQuery('(max-width:320px)');

    const [loading, setLoading] = useState(false);
    const [state, setState] = useState(STATE.PASSWORD);
    const [inputValue, setInputValue] = useState({
        phone: '',
        password: '',
        otp: '',
        confirmPassword: '',
    });
    const [OTPRef, setOTPRef] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [token, setToken] = useState(null);
    const [agree, setAgree] = useState(false);
    const [errors, setErrors] = useState([]);
    const [selectData, setSelectData] = useState({});

    useEffect(() => {
        async function isConfirm() {
            const { search } = location;
            const queryParams = new URLSearchParams(search);
            const extractToken = queryParams.get('token');
            if (extractToken) {
                try {
                    const resCheckConfirm = await checkConfirm(extractToken);
                    if (resCheckConfirm && resCheckConfirm.status === 200) {
                        setToken(extractToken);
                        setState(STATE.REQUEST);
                    } else {
                        throw new Error(resCheckConfirm);
                    }
                } catch (error) {
                    dispatch(alertError(error.response?.data?.message));
                }
            }
        }

        isConfirm();
    }, []);

    const handleChange = (e) => {
        const newInputValue = { ...inputValue };
        const value = e.target.value;
        const name = e.target.name;

        if (name === 'term') {
            setAgree(e.target.checked);
        } else {
            newInputValue[name] = value;
        }
        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const handleForgotPassword = () => {
        history.push('/provider/forgot');
    };

    const handleSuccessSignIn = (user, jwt, session) => {
        if (user.staff.length === 0) {
            return dispatch(alertError('Cannot find Staff data. Please check input value.'));
        } else {
            setState(STATE.SELECT);
            setSelectData({ role: 'Staff', id: user.id, jwt, session });
        }
    };

    useEffect(() => {
        setErrors([]);
    }, [state]);

    const handleSignIn = async () => {
        const newErrors = [];

        if (!validPhone.test(inputValue.phone) && !validEmail.test(inputValue.phone)) {
            newErrors.push({
                property: 'phone',
                message: 'Please provide phone number or email.',
            });
        }

        if (state === STATE.PASSWORD) {
            if (!inputValue.password) {
                newErrors.push({
                    property: 'password',
                    message: 'Please provide password.',
                });
            }
        } else {
            if (!inputValue.otp) {
                newErrors.push({
                    property: 'otp',
                    message: 'Please provide otp.',
                });
            }
        }

        if (newErrors.length) return setErrors(newErrors);

        setLoading(true);
        try {
            let res;
            if (state === STATE.PASSWORD) {
                res = await signInWithPassword({
                    mobileNumber: inputValue.phone,
                    password: inputValue.password,
                });
            } else {
                res = await signInWithOTP({
                    mobileNumber: inputValue.phone,
                    refCode: OTPRef,
                    otp: inputValue.otp,
                });
            }

            if (res.jwt) {
                handleSuccessSignIn(res.user, res.jwt, res.session);

                setLoading(false);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);

            if (_.isArray(error?.response?.data?.message)) {
                dispatch(
                    alertError(
                        `Cannot Signin: ${error?.response?.data?.message[0]?.messages[0]?.message}`
                    )
                );
            } else if (_.isString(error?.response?.data?.message)) {
                dispatch(alertError(`Cannot Signin: ${error?.response?.data?.message}`));
            } else {
                dispatch(alertError(`Cannot Signin: please try again later.`));
            }
        }
    };

    const handleSwitchToOTP = () => {
        if (state === STATE.PASSWORD) {
            if (countdown > 0) {
                setState(STATE.OTP);
            } else {
                setState(STATE.REQUEST);
            }
        } else {
            setState(STATE.PASSWORD);
        }

        setInputValue({ ...inputValue, password: '', otp: '' });
    };

    const handleRequestOTP = async () => {
        const newErrors = [];

        if (!validPhone.test(inputValue.phone)) {
            newErrors.push({
                property: 'phone',
                message: 'Please provide phone number.',
            });
        }

        if (newErrors.length) return setErrors(newErrors);

        setLoading(true);

        try {
            const resOTP = await requestOTP(inputValue.phone);
            if (resOTP.refCode) {
                setOTPRef(resOTP.refCode);
            } else {
                throw new Error(resOTP);
            }

            if (token) {
                setState(STATE.CONFIRM_PASSWORD);
            } else {
                setState(STATE.OTP);
            }
            setTimer();
        } catch (error) {
            dispatch(alertError(`${JSON.stringify(error.response?.data?.message)}`));
        }

        setLoading(false);
    };

    const handleResendOTP = async () => {
        setLoading(true);

        try {
            const resOTP = await requestOTP(inputValue.phone);
            if (resOTP.refCode) {
                setOTPRef(resOTP.refCode);
                setInputValue({ ...inputValue, otp: '' });
            } else {
                throw new Error(resOTP);
            }
            setTimer();
        } catch (error) {
            dispatch(alertError(`${JSON.stringify(error.response?.data?.message)}`));
        }
        setLoading(false);
    };
    const handleSignUp = async () => {
        setLoading(true);

        try {
            const OTPNumber = inputValue.otp;
            const password = inputValue.password;
            const confirmPassword = inputValue.confirmPassword;

            const newErrors = [];

            if (!password) {
                newErrors.push({ property: 'password', message: 'Please provide password.' });
            }

            if (!validPassword.test(password)) {
                newErrors.push({
                    property: 'password',
                    message:
                        'Password must be at least 8 character and special character and number.',
                });
            }

            if (!confirmPassword) {
                newErrors.push({
                    property: 'confirmPassword',
                    message: 'Please provide confirm password.',
                });
            }

            if (!(password === confirmPassword)) {
                newErrors.push({
                    property: 'confirmPassword',
                    message: 'Confirm password must equal to password.',
                });
            }

            if (newErrors.length) return setErrors(newErrors);

            const resVefify = await verifyOTP({
                OTPNumber: OTPNumber,
                refCode: OTPRef,
                users_id: token,
            });
            if (resVefify) {
                setState(STATE.TERM);
            } else {
                throw new Error(resVefify);
            }
        } catch (error) {
            dispatch(alertError(`${JSON.stringify(error.response?.data?.message)}`));
        } finally {
            setLoading(false);
        }
    };
    const handleCreateAccount = async () => {
        setLoading(true);

        try {
            const password = inputValue.password;
            const res = await completeRegister(token, password);
            if (res && res.status === 200) {
                if (res.data.jwt) {
                    handleSuccessSignIn(res.data.user, res.data.jwt);
                } else {
                    throw new Error(res);
                }
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`${JSON.stringify(error.response?.data?.message)}`));
        }

        setLoading(false);
    };

    const setTimer = () => {
        let count = 30;
        setCountdown(count);
    };

    useEffect(() => {
        if (countdown > 0) {
            setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        }
    }, [countdown]);

    const onPressEnterPhoneInput = () => {
        if (state === STATE.REQUEST) {
            handleRequestOTP();
        }
    };

    const onPressEnterOTPInput = () => {
        if (!isVerify) return;

        if (state === STATE.OTP) handleSignIn();
    };

    const onPressEnterPasswordInput = () => {
        if (!isVerify) return;

        if (state === STATE.PASSWORD) handleSignIn();
    };

    /*---------- reCAPTCHA -----------*/
    const captchaRef = useRef(null);
    const [isVerify, setIsVerify] = useState(false);

    const handleVerifySuccess = () => {
        setIsVerify(true);
    };
    const handleVerifyError = () => {
        setIsVerify(false);
    };
    const handleVerifyExpire = () => {
        setIsVerify(false);
    };

    return (
        <div
            style={{
                maxWidth: '400px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                rowGap: '20px',
                padding: '60px 0 25px',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img src={FirdayLogo} alt="friday" style={{ width: '79px' }} />
            </div>
            <Text $color="gray2" $fontSize="14px" style={{ textAlign: 'center' }}>
                FRIDAY brings fine days.
            </Text>

            {state === STATE.TERM ? (
                <StyledCard
                    style={{
                        padding: mobileS ? '40px 10px 25px' : '40px 15px 25px',
                        minHeight: '316px',
                    }}
                >
                    <StyledCardContent
                        style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                rowGap: '5px',
                                paddingLeft: '20px',
                                paddingRight: '20px',
                            }}
                        >
                            <Text $color="gray2" $fontSize="20px" $bold>
                                Term & Condition
                            </Text>
                            <Text $color="gray2" $fontSize="14px">
                                General site usage : Update Jun 2022
                            </Text>
                        </div>
                        <div
                            style={{
                                padding: '20px',
                                borderRadius: '16px',
                                backgroundColor: 'white',
                                height: '250px',
                                overflow: 'auto',
                                wordWrap: 'break-word',
                            }}
                        >
                            <Text $fontSize="14px">
                                <Span $bold style={{ textDecoration: 'underline' }}>
                                    ข้อกำหนดและเงื่อนไข
                                </Span>
                                <br />
                                <Span $bold>ข้อ 1. ขอบเขตของการบริการ</Span>
                                <br />
                                การบริการให้คำปรึกษาการแพทย์ทางไกล (telemedicine)
                                เป็นการให้บริการแบบทางไกลที่ทำให้ผู้ป่วยสามารถสื่อสารกับบุคลากรทางการแพทย์ซึ่งได้แก่
                                แพทย์ทั่วไป แพทย์เฉพาะทาง
                                หรือบุคลากรทางการแพทย์ที่มีความรู้ความเชี่ยวชาญเฉพาะทางอื่นๆ
                                ผ่านทางโทรศัพท์ ทางเสียง หรือโดยการสนทนาผ่านทางข้อความหรือทางวิดีโอ
                                เพื่อให้คำปรึกษา คำแนะนำ
                                และการวินิจฉัยโรคเพื่อให้เกิดประโยชน์สูงสุดแก่ผู้ป่วย
                                <br />
                                <Span $bold>ข้อ 2. คำจำกัดความ</Span>
                                <br /> คำต่าง ๆ
                                ดังต่อไปนี้จะมีความหมายและขอบเขตตามคำจำกัดความที่ให้ไว้ในบริบทของการใช้เงื่อนไขโดยทั่วไปเหล่านี้
                                <br />
                                <Span $bold>แพทย์</Span> หมายถึง ผู้ประกอบวิชาชีพเวชกรรม
                                <br />
                                <Span $bold>บุคลากรทางการแพทย์</Span> หมายถึง แพทย์ หรือ พยาบาล หรือ
                                บุคลากรทางการแพทย์อื่นๆ
                                ที่สำเร็จการศึกษาตามหลักสูตรอย่างถูกต้องเหมาะสม
                                เจ้าหน้าที่หรือผู้รับจ้างต่างๆ ของ Friday
                                ที่ได้รับการฝึกอบรมโดยเฉพาะเพื่อให้บริการ
                                <br />
                                <Span $bold>ทีมแพทย์</Span> หมายถึง
                                คณะให้ความช่วยเหลือทางการแพทย์ที่ดำเนินการโดยบริษัท วีคเดย์ จำกัด
                                เพื่อให้บริการ
                                <br />
                                <Span $bold>ผู้ป่วย</Span> หมายถึง บุคคล
                                หรือกลุ่มบุคคลที่ได้ลงทะเบียนขอรับบริการไว้กับ บริษัท วีคเดย์ จำกัด
                                เพื่อรับคำปรึกษา หรือการวินิจฉัยทางการแพทย์
                                นอกจากนี้ผู้ป่วยยังสามารถมอบอำนาจ หรือให้ตัวแทนที่ถูกต้องตามกฎหมาย
                                หรือบุคคลอื่นที่ได้รับการแต่งตั้งอย่างชัดเจนจากผู้ป่วยเข้าดำเนินการแทนผู้ป่วยได้
                                เพื่ออำนวยความสะดวก และเพื่อความรวดเร็วในการดำเนินการต่อไป
                                <br />
                                <Span $bold>การบริการ</Span>
                                หมายถึง การบริการให้คำปรึกษาทางไกลด้านการแพทย์ที่ดำเนินการโดยบริษัท
                                วีคเดย์ จำกัด <br />
                                ซึ่งได้แก่ (1) การสอบถามอาการผู้ป่วยเบื้องต้น (2) การให้คำปรึกษา
                                แนะนำ และวินิจฉัยโรคต่าง ๆ ให้แก่ผู้ป่วย (3)
                                การจองคิวเพื่อรับบริการผ่านทางออนไลน์ (4)
                                การบริการผ่านช่องทางการสื่อสารที่เสนอให้โดยบริษัท วีคเดย์ จำกัด และ
                                (5) การบริการทางการแทพย์ที่เกี่ยวข้องอื่นๆ
                                ที่เสนอให้ทางออนไลน์หรือผ่านทางโทรศัพท์ (การจัดส่งใบสั่งยา
                                การจัดส่งยารักษาโรค เป็นต้น)
                                <br />
                                <Span $bold>ซอฟต์แวร์</Span> หมายถึง โปร
                                แกรมคอมพิวเตอร์ที่พัฒนาขึ้นโดยบริษัท วีคเดย์ จำกัด
                                และ/หรือโดยผู้ค้าเพื่อช่วยให้ผู้ป่วย
                                และบุคลากรทางการแพทย์สามารถเข้าถึงระบบการให้บริการ
                                การจองคิวผ่านทางออนไลน์
                                ับการรักษาผ่านช่องทางการสนทนาทางข้อความและทางวิดีโอ
                                <br />
                                <Span $bold>แพทย์เฉพาะทาง</Span> จะหมายถึง
                                แพทย์ที่มีความเชี่ยวชาญทางการแพทย์เฉพาะด้าน
                                <br />
                                <Span $bold>บริการตรวจอาการ</Span> หมายถึง
                                โปรแกรมสำเร็จรูปทางออนไลน์ที่พัฒนาขึ้นและเป็นเจ้าของโดย บริษัท
                                วีคเดย์ จำกัด
                                เพื่อคัดแยกและประเมินว่าอาการที่แจ้งโดยผู้ป่วยจะสามารถให้บริการได้หรือไม่
                                หรือว่าผู้ป่วยควรได้รับการบริการช่วยเหลือทางการแพทย์อื่น ๆ
                                หรือว่าผู้ป่วยควรเดินทางไปยังสถานพยาบาลฉุกเฉินที่อยู่ใกล้ที่สุด
                                โดยไม่ว่าในกรณีใด ๆ
                                จะมีการพิจารณาเพื่อแนะนำการดำเนินการอย่างหนึ่งอย่างใดเพื่อแก้ปัญหาให้แก่ผู้ป่วยให้ดีที่สุด
                                <br />
                                <Span $bold>การให้คำปรึกษาทางไกล</Span> หมายถึง
                                การให้คำปรึกษาด้านการแพทย์ทางไกลผ่านทางโทรศัพท์ ผ่านทางเสียง
                                การสนทนาผ่านทางข้อความหรือทางวิดีโอ
                                <br />
                                <Span $bold>ข้อ 3. กรอบของกฎหมาย</Span>
                                <br />
                                การบริการเป็นการบริการด้านการแพทย์ทางไกลที่จัดทำขึ้นตามผู้ประกอบวิชาชีพเวชกรรมที่ได้รับใบอนุญาตประกอบวิชาชีพเวชกรรมของไทยที่ออกให้โดยแพทยสภาไทย
                                โดยให้บริการ [*****] ชั่วโมงต่อวัน [*****] วันต่อสัปดาห์
                                ตามประกาศของแพทยสภา ที่ 54/2563
                                ว่าด้วยแนวทางปฏิบัติการแพทย์ทางไกลหรือโทรเวช (telemedicine)
                                และคลินิกออนไลน์ (“แนวทางปฏิบัติการแพทย์ทางไกล”)
                                ในการให้บริการดังกล่าว
                                แพทย์ต้องให้บริการผ่านสถานพยาบาลที่มีใบอนุญาตที่ถูกต้องเท่านั้น
                                และการดำเนินการทางการแพทย์ดังกล่าวต้องดำเนินการตามข้อกำหนดหลักดังต่อไปนี้
                                <br />
                                3.1 แพทย์และผู้ป่วยควรทราบ
                                และจะต้องได้รับทราบถึงข้อเท็จจริงที่ว่ามีเฉพาะบางโรค
                                เท่านั้นที่เหมาะสมสำหรับการใช้การแพทย์ทางไกล
                                <br />
                                3.2 แพทย์และผู้ป่วยควรทราบ
                                และจะต้องได้รับทราบเกี่ยวกับข้อจำกัดด้านเทคโนโลยีและอิเล็กทรอนิกส์
                                ร่วมถึง สิทธิ์ของผู้ป่วยที่จะปฏิเสธการใช้ยา
                                <br />
                                3.3
                                แพทย์ต้องปฏิบัติตามเกณฑ์มาตรฐานของผู้ประกอบวิชาชีพเวชกรรมของแพทยสภา
                                พ.ศ. 2555 เช่น จะต้องมีความรู้ทางการแพทย์
                                และมีทักษะการสื่อสารและการสร้างสัมพันธภาพตามมาตรฐานที่กำหนดประกอบกับสามารถให้การดูแลรักษาผู้ป่วยได้ตามมาตรฐานที่กำหนด
                                <br />
                                3.4 การใช้เครื่องมือ โปรแกรม หรือปัญญาประดิษฐ์ (AI)
                                ร่วมกับการแพทย์ทางไกลต้องเป็นไปตามกฎหมายที่ระบุกำหนดไว้
                                และเกี่ยวข้องด้วย (เช่น
                                กฎหมายเกี่ยวกับเครื่องมือแพทย์และกฎหมายเกี่ยวกับยา) Recheck
                                เรื่องใบสั่งยา: การให้บริการจะให้บริการผ่านการสนทนาทางข้อความ
                                หรือโทรศัพท์พูดคุย
                                หรือโทรศัพท์พูดคุยผ่านวิดีโอโดยผ่านเว็บแอพพลิเคชั่นบริการที่ให้โดยบริษัท
                                วีคเดย์ จำกัด ผ่านทางเว็บแอพพลิเคชั่นออนไลน์สำหรับอุปกรณ์ตั้งโต๊ะ
                                และอุปกรณ์เคลื่อนที่ต่างๆ โดยบริษัท วีคเดย์ จำกัด
                                จะทำการจัดส่งยาประเภทที่ไม่จำเป็นต้องใช้ใบสั่งยาจากแพทย์และต้องใช้ใบสั่งยาจากแพทย์ให้แก่ป่วย
                                ซึ่งผู้ให้บริการจัดส่งสามารถเข้าถึงพื้นที่ได้โดยที่ผู้ใช้ต้องเป็นผู้ชำระค่าจัดส่งและค่ายาทั้งสองประเภทดังกล่าว
                                <br />
                                <Span $bold>ข้อ 4. ข้อสละสิทธิ์และคำเตือน</Span>
                                <br />
                                ทางบริษัทวีคเดย์ จำกัด ต้องขอแจ้งให้ผู้รับบริการทราบก่อนว่า
                                การบริการนี้ไม่ใช่การบริการแบบฉุกเฉิน หากเป็นกรณีฉุกเฉิน
                                ผู้ป่วยต้องโทรหาหมายเลขฉุกเฉินท้องถิ่น
                                หรือไปที่หน่วยฉุกเฉินของสถานพยาบาลที่อยู่ใกล้ที่สุด
                                การบริการนี้ไม่สามารถนำมาใช้ทดแทนการบริการที่ผู้ป่วยรับบริการจากแทพย์ที่ให้การรักษาผู้ป่วยเป็นประจำได้
                                เป็นเพียงการบริการเสริมที่มีให้ในกรณีที่แพทย์ที่ทำการรักษาประจำไม่สามารถให้บริการได้
                                เราแนะนำให้ผู้ป่วยแจ้งกับแพทย์ที่ตนทำการรักษาเป็นประจำเกี่ยวกับข้อมูลใดๆ
                                ที่ผู้ป่วยได้พูดคุยสื่อสารกับแพทย์ที่ให้บริการผ่านการให้บริการนี้
                                <br />
                                <Span $bold>ข้อ 5. การยอมรับเงื่อนไขโดยทั่วไป</Span>
                                <br />
                                ก่อนการให้บริการ
                                ผู้ป่วยจะต้องตกลงยอมรับเงื่อนไขโดยทั่วไปข้างต้นนี้ก่อนอย่างชัดแจ้ง
                                และตกลงว่าจะปฏิบัติตามข้อกำหนดที่ระบุไว้ด้านล่างนี้อย่างครบถ้วนสมบูรณ์
                                ซึ่งข้อตกลงดังกล่าวจะมีผลบังคับใช้ตลอดระยะเวลาการบริการ
                                อย่างไรก็ตามเนื่องจากอาจมีการเปลี่ยนแปลงเกี่ยวกับกฎหมาย กฎ ข้อบังคับ
                                หรือระเบียบของท้องถิ่นภายในหรือระหว่างประเทศ
                                รวมถึงการแก้ไขเพิ่มเติมเกี่ยวกับการบริการ ดังนั้น บริษัทวีคเดย์
                                จำกัด จึงขอสงวนสิทธิ์ที่จะแก้ไขเพิ่มเติมเงื่อนไขโดยทั่วไปเหล่านี้ ณ
                                เวลาใดๆ ก็ตาม นอกจากนี้
                                เงื่อนไขที่ได้มีการปรับปรุงแก้ไขใหม่จะมีผลบังคับใช้กับผู้ป่วยรายใหม่ใดๆ
                                <br />
                                <Span $bold>ข้อ 6. การบริการ</Span> [เพิ่มเติมตามผู้พัฒนา Friday]
                                <br />
                                <Span $bold>6.1 รายละเอียดของการบริการ</Span>
                                <br />
                                การบริการประกอบไปด้วยขั้นตอนสำคัญซึ่งได้ระบุรายละเอียดไว้ด้านล่างนี้
                                (1) การซักถามผู้ป่วยเบื้องต้นผ่านบริการตรวจอาการ (2)
                                การจองเพื่อรับบริการผ่านทางออนไลน์ (3) การให้คำปรึกษา แนะนำ
                                และวินิจฉัยโรคให้แก่ผู้ป่วย (4)
                                การบริการผ่านช่องทางการสื่อสารที่เสนอให้โดยบริษัทวีคเดย์ จำกัด และ
                                (5) การบริการทางการแพทย์ที่เกี่ยวข้องอื่นๆ
                                ทางออนไลน์หรือผ่านทางโทรศัพท์ (การจัดส่งใบสั่งยา การจัดส่งยารักษา
                                เป็นต้น)
                                <br />
                                <Span $bold>6.2 การซักถามผู้ป่วยเบื้องต้นผ่านบริการตรวจอาการ</Span>
                                <br />
                                ผู้ป่วยจะแจ้งอาการของตนและจะได้รับการพิจารณาเพื่อจัดให้ได้รับการบริการช่วยเหลือที่เหมาะสมที่สุด
                                ได้แก่ การให้คำปรึกษาทางไกลกับแพทย์ทั่วไป หรือ แพทย์เฉพาะทาง
                                หรือการสนับสนุนความช่วยเหลือทางการแพทย์ผ่านศูนย์ติดต่อของเราตลอด 24
                                ชั่วโมงทุกวัน นที่อยู่ใกล้ที่สุด
                                <br />
                                <Span $bold>6.3 การจองเพื่อรับบริการผ่านทางออนไลน์</Span>
                                <br />
                                ผู้ป่วยจะเลือกช่องทางการสื่อสารที่ตนต้องการ (ทางโทรศัพท์
                                ผ่านทางเสียง ผ่านวิดีโอ การสนทนาผ่านทางข้อความ)
                                ผู้ป่วยสามารถเลือกระบุความต้องการเฉพาะ แพทย์ (ทางเลือก)
                                และวันและเวลาเพื่อรับบริการ ทางเลือกต่างๆ
                                ที่มีให้เป็นไปตามทางเลือกที่บริษัทวีคเดย์ จำกัด เสนอให้ในแต่ละท้องที
                                <br />
                                <Span $bold>
                                    6.4 การให้คำปรึกษา แนะนำ และวินิจฉัยโรคให้แก่ผู้ป่วย
                                </Span>
                                <br />
                                เมื่อทราบข้อเท็จจริงและอาการต่างๆของผู้ป่วยแล้ว
                                แพทย์ของทางบริษัทวีคเดย์ จำกัด จะทำการตรวจวินิจฉัยเพื่อให้ทราบโรค
                                หรืออาการของผู้ป่วยให้ถูกต้องเพื่อจะได้ทำการรักษาต่อไป ณ
                                วันและเวลาที่รับบริการ ผู้ป่วยสามารถเชื่อมต่อได้โดยตรง
                                (ผ่านทางวิดีโอ เสียงและการสนทนาผ่านทางข้อความ)
                                จากลิงค์ที่ผู้ป่วยได้รับผ่านทางอีเมล์
                                หากเป็นการรับบริการผ่านทางโทรศัพท์
                                แพทย์อาจติดต่อผู้ป่วยโดยตรงทางหมายเลขที่แจ้งไว้
                                ระยะเวลาการบริการตามกำหนดขึ้นอยู่กับดุลพินิจของแพทย์ <br />
                                <Span $bold>6.5 การบริการทางการแพทย์ที่เกี่ยวข้องอื่นๆ</Span>
                                <br />
                                การบริการทางการแทพย์อื่นๆ ที่สามารถให้บริการได้ ได้แก่
                                การจัดส่งใบสั่งยา การจัดส่งยารักษา การติดตามผลทางการแพทย์
                                เฉพาะการบริการที่ระบุไว้ในสัญญาเท่านั้นที่มีบริการให้แก่ผู้ป่วย
                                <br />
                                <Span $bold>6.6 การเข้าถึงบริการ</Span>
                                <br />
                                เพื่อเข้าถึงการบริการ
                                ผู้ป่วยจะเชื่อมต่อกับช่องทางการให้บริการด้านสุขภาพของบริษัทวีคเดย์
                                จำกัด โดยใส่หมายเลขอ้างอิงประจำตัวของตน (PNR/หมายเลขกรมธรรม์)
                                ที่ผู้ป่วยจะได้รับเมื่อทำสัญญา และปฏิบัติตามข้อตกลงและคำแนะนำต่างๆ
                                ที่ได้กำหนดไว้
                                หลังจากผ่านขั้นตอนบริการตรวจอาการและขั้นตอนการจองเพื่อรับบริการผ่านทางออนไลน์แล้ว
                                ผู้ป่วยจะได้รับคำยืนยันรายละเอียดต่างๆ
                                เกี่ยวกับการนัดหมายของตนผ่านทางอีเมล์ ก่อนเวลานัดหมาย 10 นาที
                                ผู้ป่วยจะได้รับอีเมล์อีกหนึ่งฉบับซึ่งจะมีลิงค์เพื่อใช้เชื่อมต่อหากผู้ป่วยเลือกที่จะรับบริการให้คำปรึกษาทางไกลผ่านการสนทนาหรือสนทนาผ่านทางวิดีโอ
                                หากผู้ป่วยขอรับบริการโดยให้ติดต่อกลับทางโทรศัพท์
                                แพทย์ของเราจะติดต่อผู้ป่วยโดยตรงผ่านทางหมายเลขโทรศัพท์ที่ผู้ป่วยได้แจ้งไว้
                                <br />
                                <Span $bold>6.7 การแก้ไขเปลี่ยนแปลง และการยกเลิกการนัดหมาย</Span>
                                <br />
                                และการยกเลิกการนัดหมาย
                                ผู้ป่วยสามารถที่จะแก้ไขเปลี่ยนแปลงและ/หรือยกเลิกการนัดหมายได้ก่อนเวลานัดหมาย
                                30 นาที ในอีเมล์ที่ผู้ป่วยได้รับเมื่อยืนยันการจอง
                                จะมีลิงค์เข้าหน้าเว็บที่มีปุ่มที่สามารถยกเลิกการนัดหมายได้
                                การแก้ไขเปลี่ยนแปลงการนัดหมาย
                                ผู้ป่วยต้องยกเลิกการนัดหมายเดิมที่มีอยู่ก่อนและเลือกช่วงเวลาใหม่ที่ตนต้องการหากยังมีเวลาว่างอยู่
                                <br />
                                <Span $bold>
                                    6.8 การให้บริการผ่านการสนทนาทางข้อความและการสนทนาผ่านทางวิดีโอ
                                </Span>
                                <br />
                                ผู้ป่วยไม่จำเป็นต้องรอจนถึงเวลาที่นัดหมายจริงๆ ถึงจะกดปุ่ม
                                “เริ่มการปรึกษาทางไกลของข้าพเจ้า”
                                ที่อยู่ในอีเมล์ที่ผู้ป่วยจะได้รับก่อนเวลานัดหมาย 10 นาที
                                หากผู้ป่วยกดปุ่มดังกล่าวก่อนเวลาที่นัดหมาย
                                ผู้ป่วยจะเข้าสู่ห้องรอรับบริการเสมือนจริงจนกว่าแพทย์ของเราจะได้รับการเชื่อมต่อและพร้อมเริ่มให้บริการ
                                เมื่อแพทย์ได้รับการเชื่อมต่อเพื่อให้บริการ จะปรากฏข้อความตอนรับขึ้น
                                และ หากเลือกรับบริการผ่านการสนทนาผ่านทางข้อความ
                                ผู้ป่วยและแพทย์จะสามารถเริ่มพูดคุยโต้ตอบกันได้
                                หากเลือกรับบริการผ่านการสนทนาผ่านทางวิดีโอ
                                ผู้ป่วยต้องให้ความยินยอมการใช้งานวิดีโอก่อนและหน้าต่างวิดีโอจะเปิดขึ้นมา
                                ผู้ป่วยสามารถเลือกที่จะไม่เปิดวิดีโอและพูดคุยกับแพทย์ผ่านทางเสียงเท่านั้น
                                และผู้ป่วยต้องเข้ารับบริการตามเวลาที่นัดหมายไว้
                                ผู้ป่วยอาจเข้ารับบริการล่าช้าและสามารถเชื่อมต่อได้จนถึง 10
                                นาทีก่อนที่จะหมดเวลา 15 นาที หรือ 30
                                นาทีที่ได้กำหนดไว้สำหรับการรับบริการจากแพทย์ หลังจากเวลาดังกล่าว
                                เราไม่รับประกันว่าแพทย์จะยังคงเชื่อมต่อเพื่อรอให้บริการอยู่หรือไม่
                                และในกรณีใดๆ
                                จะไม่สามารถพูดคุยโต้ตอบได้เกินกว่าระยะเวลาเดิมที่กำหนดไว้
                                หากต้องการเวลาเพิ่ม และแพทย์ของเราไม่มีนัดหมายอื่น ทางบริษัทวีคเดย์
                                จำกัด สามารถอนุญาตให้เวลาเพิ่มเติมได้ หากแพทย์ไม่ว่างให้บริการ
                                แพทย์จะแนะนำให้ผู้ป่วยทำการนัดหมายใหม่อีกครั้ง
                                ตลอดการสนทนาพูดคุยกับแพทย์ของเรา
                                ทั้งผู้ป่วยและแพทย์สามารถแลกเปลี่ยนเอกสารต่างๆ
                                ผ่านการสนทนาหรือเริ่มสนทนาโดยตรง หากเชื่อมต่อผ่านทางโทรศัพท์มือถือ
                                ผู้ป่วยสามารถถ่ายรูปได้โดยตรงด้วยกล้องของโทรศัพท์และส่งรูปผ่านโปรแกรมการสนทนาที่ให้บริการอยู่ของเรา
                                <br />
                                <Span $bold>6.9 การให้บริการผ่านโทรศัพท์</Span>
                                <br />
                                หากผู้ป่วยเลือกให้ติดต่อทางโทรศัพท์ ย์ของทางบริษัทวีคเดย์ จำกัด
                                จะโทรศัพท์หาผู้ป่วยทางโทรศัพท์มือถือหรือโทรศัพท์พื้นฐานที่ผู้ป่วยได้แจ้งไว้
                                ผู้ป่วยต้องรับการบริการตามเวลาที่นัดหมายและสัญญาณโทรศัพท์เพียงพอสำหรับการโทรศัพท์เพื่อใช้บริการ
                                แพทย์ของเราจะพยายามติดต่อเพิ่มอีก 2 ครั้งในทุก 5 นาที
                                หากผู้ป่วยไม่รับโทรศัพท์
                                ผู้ป่วยจะต้องทำการนัดหมายใหม่อีกครั้งเพื่อรับบริการ <br />
                                <br />
                                <br />
                                <Span $bold>6.10 แพทย์ของเราไม่มาให้บริการ</Span>
                                <br />
                                แม้ว่าเราจะใช้ความพยายามอย่างดีที่สุด
                                แพทย์ของเราที่กำหนดไว้สำหรับการนัดหมายของผู้ป่วยอาจไม่ว่าง ณ
                                เวลาที่กำหนด หากแพทย์ยังไม่ว่างเกินว่า 5 นาที
                                จะมีการส่งต่อการขอรับบริการโดยอัตโนมัติไปยังแพทย์ท่านอื่นที่ว่างอยู่เพื่อให้บริการแก่ผู้ป่วย
                                หากไม่มีแพทย์ว่าง
                                จะมีข้อความปรากฏขึ้นเพื่อแนะนำให้ผู้ป่วยทำการจองวันและเวลาใหม่อีกครั้งเพื่อรับบริการให้คำปรึกษาทางไกล
                                หากผู้ป่วยขาดการเชื่อมต่อก่อน 5 นาที
                                เจ้าหน้าที่ดูแลของเราจะได้รับข้อความเตือนและจะโทรศัพท์หาผู้ป่วยเพื่อควบคุมดูแลสิ่งที่เกิดขึ้น
                                หากผู้ป่วยยืนยันว่าตนยังคงต้องการที่จะพูดคุยกับแพทย์
                                เจ้าหน้าที่ดูแลของเราจะเชื่อมต่อให้ผู้ป่วยได้คุยกับแพทย์ของเราทางโทรศัพท์ทันทีหรือเสนอให้ทำการจองเวลาใหม่
                                หากแพทย์ไม่ว่าง
                                <br />
                                <Span $bold>
                                    6.11
                                    ผู้ป่วยที่เป็นผู้เยาว์หรือเป็นผู้ที่อยู่ในความอุปการะของบุคคลอื่นตามกฎหมาย
                                </Span>
                                <br />
                                ในกรณีผู้ป่วยเป็นผู้เยาว์หรือเป็นบุคคลที่อยู่ในความอุปการะของบุคคลอื่นตามกฎหมาย
                                ผู้ป่วยต้องได้รับความช่วยเหลือจากตัวแทนที่ชอบด้วยกฎหมายที่ได้รับอนุญาตเท่านั้น
                                การรับรองยืนยันเกี่ยวกับเรื่องดังกล่าวอยู่ภายใต้ความรับผิดชอบแต่เพียงผู้เพียงของผู้รับรองยืนยันดังกล่าว
                                <br />
                                <Span $bold>6.12 การบริการที่เกี่ยวข้องอื่นๆ</Span>
                                <br />
                                การติดตามผลหลังการให้คำปรึกษาทางไกล หากปรากฏว่า
                                แพทย์ของเราพิจารณาเห็นว่าจำเป็น
                                จะมีการดำเนินจัดให้มีการติดตามผลหลังการให้คำปรึกษาทางไกลโดยแพทย์ของเรา
                                ณ วันและเวลาที่ทั้งสองฝ่ายสะดวก
                                หากเกี่ยวกับการตรวจที่แพทย์ของเราแนะนำ
                                และในกรณีที่ผู้ป่วยไม่สามารถทำการตรวจดังกล่าวได้หรือยังไม่ได้รับผลการตรวจก่อนวันและเวลานัดหมาย
                                ผู้ป่วยจะแจ้ง บริษัทวีคเดย์ จำกัด เพื่อเลื่อนการนัดหมาย
                                การดำเนินการดังกล่าวสามารถดำเนินการได้ผ่านทางโทรศัพท์
                                โดยโทรศัพท์มาที่ศูนย์ติดต่อของเราได้ตลอด 24 ชั่วโมงทุกกัน ที่หมายเลข
                                [*****]
                                <br />
                                <Span $bold>
                                    ข้อ 7. ข้อยกเว้น ที่ทาง บริษัทวีคเดย์ จำกัด จะไม่ให้การรับรอง
                                </Span>
                                <br />
                                7.1 การร้องขอรับบริการใดๆ ที่พิจารณาเห็นว่าไม่เหมาะสมทางการแพทย์
                                หรือไม่เกี่ยวข้องกับทางการแพทย์ จะไม่ได้รับการให้บริการแต่อย่างใด
                                <br />
                                7.2 หากมีสถานการณ์หรือภาวะฉุกเฉินเกิดขึ้น
                                งกล่าวจะขึ้นอยู่กับการตัดสินใจของแพทย์ทางบริษัทวีคเดย์ จำกัด
                                แต่เพียงผู้เดียว นอกจากนี้
                                บริษัทฯแนะนำให้ผู้ป่วยโทรหมายเลขฉุกเฉินท้องถิ่นหรือไปยังหน่วยฉุกเฉินของสถานพยาบาลที่อยู่ใกล้ที่สุด
                                <br />
                                7.3
                                การโทรศัพท์เพื่อขอรับบริการโดยผู้ป่วยที่เป็นผู้เยาว์หรือเป็นผู้ที่อยู่ในอุปการะของบุคคลอื่นโดยที่ไม่มีบุคคลที่ได้รับอนุญาตให้การช่วยเหลือ
                                <br />
                                7.4 ใบสั่งยาเพื่อซื้อยาในครั้งใหม่เพื่อรักษาโรคเรื้อรัง <br />
                                7.5 ใบรับรองแพทย์
                                <br />
                                <Span $bold>ข้อ 8. ข้อมูลส่วนบุคคล</Span>
                                <br />
                                บริษัทวีคเดย์ จำกัด
                                จะรับผิดชอบเกี่ยวกับการประมวลผลข้อมูลส่วนบุคคลที่ใช้ในการให้บริการ
                                ข้อมูลของผู้ป่วยที่ส่งให้บริษัทวีคเดย์ จำกัด
                                รวมถึงข้อมูลส่วนตัวและข้อมูลด้านสุขภาพเพื่อให้บริษัทวีคเดย์ จำกัด
                                และแพทย์ของเราสามารถให้บริการแก่ผู้ป่วยได้
                                ผู้ป่วยจะได้รับแจ้งเกี่ยวกับสิทธิของตนก่อนการให้บริการแต่ละครั้ง
                                และให้ความยินยอมต่อการเก็บรวบรวม
                                การดำเนินการและจัดเก็บข้อมูลด้านสุขภาพของตนในระหว่างระยะเวลาการให้บริการทั้งหมด
                                โดยเฉพาะจะแจ้งให้ผู้ป่วยทราบถึงความอ่อนไหวของข้อมูลด้านสุขภาพของผู้ป่วย
                                นอกจากนั้น
                                ยังได้รับการคุ้มครองโดยข้อกำหนดการเก็บรักษาความลับทางการแพทย์
                                และตามกฎหมายที่มีผลบังคับใช้ บริษัทวีคเดย์ จำกัด
                                จะแจ้งให้ผู้ป่วยทราบว่า จะเก็บรักษาข้อมูลของผู้ป่วยไว้ [*****] วัน
                                และจะเก็บรักษาไว้กับบริษัทวีคเดย์ จำกัด
                                หรือบริษัทเก็บรักษาข้อมูลด้านสุขภาพที่ได้รับการรับรอง
                                และจะแจ้งให้ผู้ป่วยทราบว่าข้อมูลส่วนตัวและข้อมูลด้านสุขภาพที่ส่งให้บริษัทวีคเดย์
                                จำกัด จำเป็นสำหรับการบริการ หากไม่ดำเนินการดังกล่าว
                                ผู้ป่วยจะไม่สามารถได้รับประโยชน์จากการบริการที่ให้โดยแพทย์ของเรา
                                นอกจากนี้ผู้ป่วยสามารถคัดค้านการประมวลผลข้อมูลส่วนบุคคลของตนที่ไม่เกี่ยวข้อง
                                หรือไม่มีประโยชน์ต่อการรับบริการได้
                                โดยสามารถร้องขอให้ลบข้อมูลของตนได้ โดยผู้ป่วยต้องติดต่อบริษัทวีคเดย์
                                จำกัด ผ่านทาง [*****]
                                <br />
                                <Span $bold>ข้อ 9. ความรับผิดชอบของบริษัทวีคเดย์ จำกัด</Span>
                                <br />
                                9.1 บริษัทวีคเดย์ จำกัด ไม่ขอรับผิดชอบสำหรับความเสียหายใดๆ
                                ที่เนื่องมาจากไวรัส ข้อบกพร่องหรือแม้แต่โปรแกรมหรือแอพพลิเคชั่นใดๆ
                                ที่ไม่สามารถใช้งานร่วมกับระบบพื้นฐานที่ผู้ป่วยใช้ได้
                                หรือความเสียหายที่เกิดขึ้นกับผู้ป่วยอันเนื่องมาจากการหยุดชะงัก
                                การบำรุงรักษา ปัญหาทางเทคนิค
                                ไฟฟ้าดับหรือปัญหาเกี่ยวกับเครือข่ายสื่อสาร การใช้งานมากเกินไป
                                การให้บริการของเครือข่ายสื่อสารที่ไม่ต่อเนื่อง รวมถึง อินเตอร์เน็ต
                                การละเว้น การประมาณเลินเล่อ
                                หรือการไม่สามารถมารับบริการได้ของบุคคลที่สามหรือของผู้ป่วย
                                หรือการไม่สามารถใช้งานร่วมกันได้ของคอมพิวเตอร์ของผู้ป่วย <br />
                                9.2
                                ผู้ป่วยแต่ละรายจะรับผิดชอบสำหรับการจัดให้มีโปรแกรมป้องกันไวรัสในอุปกรณ์ของตนเองที่ใช้เพื่อเข้าถึงการบริการและสำหรับการดำเนินมาตรการด้านความปลอดภัยเพื่อป้องกันการแพร่กระจายของไวรัสและ
                                โดยทั่วไป
                                การปฏิบัติการและการดูแลที่เหมาะสมของอุปกรณ์เพื่อให้ตนสามารถได้รับประโยชน์จากการบริการ
                                รวมถึงค่าสมัครและค่าการเชื่อมต่ออินเตอร์เน็ต <br />
                                9.3 บริษัทวีคเดย์ จำกัด
                                ไม่รับผิดชอบสำหรับการไม่สามารถให้บริการได้หรือความล่าช้าในการให้บริการที่เป็นสาเหตุมาจากเหตุการณ์ที่อยู่นอกเหนือการควบคุม
                                (“กรณีเหตุสุดวิสัย”) เช่น อัคคีภัย, การระเบิด, พายุ, น้ำท่วม,
                                แผ่นดินไหว, การทรุดตัวของแผ่นดิน, โรคระบาดหรือภัยทางธรรมชาติอื่นๆ,
                                การกระทำต่างๆที่เกิดจากการบังคับใช้พระราชบัญญัติ พระราชกำหนด กฎหมาย
                                กฎระเบียบหรือข้อกำหนดของรัฐบาลทั้งหมด ฯลฯ <br />
                                9.4 บริษัทวีคเดย์ จำกัด
                                จะรับผิดชอบสำหรับความเสียหายทางตรงและที่สามารถคาดการณ์ได้ที่เกี่ยวข้องกับการใช้งานซอฟต์แวร์ที่ใช้สำหรับการให้บริการเท่านั้น
                                ไม่รวมความเสียหายทางอ้อมใดๆ <br />
                                9.5 บริษัทวีคเดย์ จำกัด จะไม่รับผิดชอบสำหรับความเสียหายใดๆ
                                ที่เป็นผลมาจาก <br />
                                9.5.1 การส่งข้อมูลใดๆ เกี่ยวกับสุขภาพของผู้ป่วย
                                โดยผู้ป่วยเองให้แก่บุคคลที่สาม
                                และโดยเฉพาะข้อมูลทางการแพทย์ที่จัดทำโดยแพทย์ของเราสำหรับการให้บริการ
                                <br />
                                9.5.2
                                การเปิดเผยลิงค์และข้อมูลการยืนยันตัวตนเพื่อเชื่อมต่อเข้ารับบริการของผู้ป่วยให้บุคคลที่สามทราบ
                                <br />
                                9.5.3
                                ผู้ป่วยใช้รายละเอียดการเชื่อมต่อของผู้ป่วยอื่นเพื่อเข้ารับบริการ
                                <br />
                                9.5.4 ผู้ป่วยส่งข้อมูลทางการแพทย์และ/หรือเอกสารที่ไม่ครบถ้วน
                                มีข้อผิดพลาด
                                ขาดหายหรือมีข้อบกพร่องให้แก่แพทย์ของเราซึ่งเป็นประโยชน์และ/หรือจำเป็นสำหรับการให้บริการโดยแพทย์ของเรา
                                <br />
                                <Span $bold>ข้อ 10. ความรับผิดชอบของผู้ป่วย</Span>
                                <br /> 10.1
                                ผู้ป่วยต้องเก็บรักษาลิงค์ที่จะใช้เชื่อมต่อเพื่อรับบริการไว้เป็นความลับอย่างเคร่งครัดและไม่เปิดเผยให้บุคคลที่สาม
                                และ โดยทั่วไป บุคคลที่สามใดๆ
                                ทราบโดยไม่คำนึงว่าบุคคลที่สามดังกล่าวจะมีคุณสมบัติและกิจกรรมการประกอบวิชาชีพอย่างไรก็ตาม
                                <br />
                                10.2 ปฏิบัติตามกฎการเข้าถึงบริการ โดยเฉพาะ
                                ไม่ใช้ลิงค์ของผู้ป่วยอื่นหรือพยายามค้นหาข้อมูลดังกล่าว <br />
                                10.3 ต้องแจ้งให้ บริษัทวีคเดย์ จำกัด
                                ทราบเกี่ยวกับความผิดปกติทางเทคนิคใดๆ ที่เกิดขึ้นและความผิดปกติใดๆ
                                ที่พบ <br />
                                10.4 ให้ข้อมูลที่เป็นประโยชน์ ที่จำเป็น
                                ที่ครบถ้วนสมบูรณ์และที่ถูกต้องเกี่ยวกับภาวะสุขภาพของตนเพื่อให้สามารถให้บริการได้
                                <br />
                                <Span $bold>ข้อ 11. ในการใช้บริการ ผู้ป่วยตกลงที่จะไม่</Span>
                                <br /> 11.1
                                ส่งหรือส่งผ่านด้วยวิธีการอื่นใดไปยังหรือผ่านซอฟต์แวร์ที่ใช้สำหรับการให้บริการซึ่งสิ่งใดๆ
                                ที่ไม่ชอบด้วยกฎหมาย ฝ่าฝืน เป็นอันตราย เป็นการละเมิด
                                เป็นการหมิ่นประมาท เป็นการข่มขู่ มีเจตนาร้าย
                                หรือก่อความรำคาญในลักษณะอื่นใดๆ สิ่งใดๆ
                                ที่สามารถเป็นเหตุให้เกิดความเสียหายหรือทำให้เกิดความล่าช้าต่อซอฟต์แวร์หรือคอมพิวเตอร์ในลักษณะใดๆ
                                โฆษณา การชักชวนหรือการส่งเสริมการขายที่ไม่พึงประสงค์ใดๆ <br />
                                11.2 การบิดเบือนข้อมูลส่วนบุคคลหรือสิ่งที่เกี่ยวข้องในลักษณะใดๆ
                                <br />
                                11.3 เข้าถึงซอฟต์แวร์หรือบริการโดยไม่ได้รับอนุญาต
                                เข้าถึงลิงค์ของผู้ป่วยอื่น
                                ข้อมูลที่สามารถระบุตัวตนได้หรือข้อมูลอื่นๆ ของผู้ป่วยอื่น
                                หรือเข้าถึงคอมพิวเตอร์หรือเว็บไซต์อื่นที่เชื่อมต่อหรือเชื่อมโยงไปยังซอฟต์แวร์
                                ส่งหรือส่งผ่านในลักษณะอื่นใดไปยังหรือผ่านซอฟต์แวร์ซึ่งจดหมายเวียน
                                ข้อความที่ไม่พึงประสงค์ หรือที่เรียกว่าข้อความ “ไม่พึงประสงค์” หรือ
                                “หลอกลวง” หรือข้อความเพื่อการทำการตลาดหรือโฆษณาสินค้าหรือบริการ
                                <br />
                                11.4
                                ละเมิดกฎหมายหรือระเบียบที่บังคับใช้หรือเงื่อนไขโดยทั่วไปเหล่านี้
                                <br />
                                11.5 แก้ไขหรือดัดแปลงส่วนใดๆ
                                ของเนื้อหาหรือบริการที่เสนอให้ผ่านซอฟต์แวร์
                                <br />
                                <Span $bold>ข้อ 12. การเรียกร้อง</Span>
                                <br />
                                การเรียกร้องใดๆ
                                ที่เกี่ยวข้องกับการใช้บริการหรือซอฟต์แวร์ควรส่งโดยผู้ให้บริการจัดส่งที่มีใบรับทราบการได้รับไปยัง
                                [*****]
                                <br />
                                <Span $bold>ข้อ 13. ภาษาและกฎหมายที่บังคับใช้</Span>
                                <br />
                                ภาษาของเอกสารข้อมูลเงื่อนไขโดยทั่วไปฉบับนี้คือภาษาอังกฤษและภาษาไทย
                                และอ้างอิง และปรับใช้ตามกฎหมายของประเทศไทย
                            </Text>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                rowGap: '10px',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Checkbox
                                label={
                                    <span style={{ marginLeft: '10px', fontSize: '14px' }}>
                                        I have and accept the term & condition
                                    </span>
                                }
                                checked={agree}
                                name="term"
                                onChange={(e) => handleChange(e)}
                            />
                            <ContainedButton
                                $noShadow
                                $width="215px"
                                onClick={handleCreateAccount}
                                disabled={!agree}
                                loading={loading}
                            >
                                Sign In
                            </ContainedButton>
                        </div>
                    </StyledCardContent>
                </StyledCard>
            ) : state === STATE.SELECT ? (
                <div
                    style={{
                        marginTop: '20px',
                        marginBottom: '20px',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        rowGap: '20px',
                    }}
                >
                    <Text $bold $fontSize="18px">
                        Choose Counseling Service Account
                    </Text>
                    <SelectHospital data={selectData} />
                </div>
            ) : (
                <StyledCard
                    style={{
                        padding: mobileS ? '40px 5px 25px' : '40px 25px 25px',
                        minHeight: '316px',
                    }}
                >
                    <StyledCardContent
                        style={{
                            display: 'grid',
                            gridTemplateRows: '1fr minmax(auto,80%)',
                            height: '100%',
                        }}
                    >
                        {token ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    rowGap: '10px',
                                    marginBottom: '20px',
                                }}
                            >
                                <Text $color="gray2" $fontSize="20px" $bold>
                                    {state === STATE.CONFIRM_PASSWORD
                                        ? 'Create your password'
                                        : 'Input your phone number'}
                                </Text>
                                <Text $color="gray2" $fontSize="14px">
                                    {state === STATE.CONFIRM_PASSWORD
                                        ? 'For first sign in. Please enter your mobile phone to request OTP for create your password.'
                                        : 'For first sign in. Please enter your mobile phone to request OTP for create your password.'}
                                </Text>
                            </div>
                        ) : (
                            <Text $color="gray2" $fontSize="24px" $bold>
                                Sign in
                            </Text>
                        )}

                        <SignInForm
                            data={inputValue}
                            onChange={handleChange}
                            onForgotPassword={handleForgotPassword}
                            onResendOTP={handleResendOTP}
                            OTPRef={OTPRef}
                            state={state}
                            countdown={countdown}
                            onPressEnterPhoneInput={onPressEnterPhoneInput}
                            onPressEnterOTPInput={onPressEnterOTPInput}
                            onPressEnterPasswordInput={onPressEnterPasswordInput}
                            errors={errors}
                            phoneEmailInput
                        />
                        <div style={{ margin: '10px 0' }}>
                            <ReCAPTCHA
                                ref={captchaRef}
                                handleSuccess={handleVerifySuccess}
                                handleError={handleVerifyError}
                                    handleExpire={handleVerifyExpire}
                            />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'flex-end',
                                marginTop: '10px',
                            }}
                        >
                            {(state === STATE.PASSWORD || state === STATE.OTP) && (
                                <ContainedButton
                                    $noShadow
                                    $width="215px"
                                    onClick={handleSignIn}
                                    loading={loading}
                                    disabled={!isVerify}
                                >
                                    Sign In
                                </ContainedButton>
                            )}
                            {state === STATE.REQUEST && (
                                <ContainedButton
                                    $noShadow
                                    $width="215px"
                                    onClick={handleRequestOTP}
                                    loading={loading}
                                    disabled={!isVerify}
                                >
                                    Request OTP
                                </ContainedButton>
                            )}
                            {state === STATE.CONFIRM_PASSWORD && (
                                <ContainedButton
                                    $noShadow
                                    $width="215px"
                                    onClick={handleSignUp}
                                    style={{ marginTop: '5px' }}
                                    loading={loading}
                                >
                                    CREATE
                                </ContainedButton>
                            )}
                        </div>
                    </StyledCardContent>
                </StyledCard>
            )}

            {!token && state !== STATE.SELECT && (
                <Text
                    $color="gray1"
                    $fontSize="14px"
                    style={{ textDecoration: 'underline' }}
                    onClick={handleSwitchToOTP}
                >
                    {state === STATE.PASSWORD ? 'Sign in with OTP' : 'Sign in with Password'}
                </Text>
            )}
            {(state !== STATE.CONFIRM_PASSWORD || state !== STATE.TERM) && (
                <Text
                    $color="gray2"
                    $fontSize="14px"
                    style={{ marginTop: '50px', textAlign: 'center' }}
                >
                    Operated by Weekday Co., Ltd.
                </Text>
            )}
        </div>
    );
};

SignIn.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default SignIn;
